<template>
  <component
    :is="element"
    class="product-prices"
    :class="{ [$class('loading')]: loading }"
  >
    <span
      v-if="varies"
      class="product-prices__varies"
      :class="classPrice"
      v-text="$string('product.prices.from')"
    />

    <span
      v-if="formattedCompareAtPrice && !priceFirst"
      class="product-prices__compare-at-price"
      :class="classCompareAtPrice"
      v-text="formattedCompareAtPrice"
    />

    <span
      class="product-prices__price"
      :class="[
        classPrice,
        { 'product-prices__price--on-sale': formattedCompareAtPrice },
      ]"
      v-text="formattedPrice"
    />

    <span
      v-if="formattedCompareAtPrice && priceFirst"
      class="product-prices__compare-at-price"
      :class="classCompareAtPrice"
      v-text="formattedCompareAtPrice"
    />

    <span
      v-if="rrpPrice"
      :class="[
        classPrice
      ]"
      class="product-prices__rrp-price"
    >
      <span class="product-prices__rrp-price-seperator">|</span>

      <span
        class="product-prices__rrp-price-text"
        v-text="formattedRrpPrice"
      />
    </span>
  </component>
</template>

<script>

/**
 * Vue: Product prices (product-prices)
 * -----------------------------------------------------------------------------
 * Global product prices template.
 *
 * @param {String} [classCompareAtPrice] - Comare at price text classes.
 * @param {String} [classPrice] - Price text classes.
 * @param {Number|Boolean} compareAtPrice - Compare at price, in cents format.
 * @param {String} [element] - Custom element override.
 * @param {Boolean} [loading] - Loading state.
 * @param {Number} price - Product price, in cents format.
 * @param {Boolean} [priceFirst] - Display price before compare at price.
 * @param {Number} [rrpPrice] - Display RRP price.
 * @param {Boolean} [varies] - Product variants vary in price.
 *
 */
export default {
  name: 'ProductPrices',

  props: {
    classCompareAtPrice: {
      type: String,
      default: 'text-other-sale-s text-other-sale-l-desktop',
    },
    classPrice: {
      type: String,
      default: 'text-body-s-regular text-body-m-regular-desktop',
    },
    compareAtPrice: {
      type: [Boolean, Number],
      default: false,
      required: true,
    },
    element: {
      type: String,
      default: 'p',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    price: {
      type: [Boolean, Number],
      default: 0,
      required: true,
    },
    priceFirst: {
      type: Boolean,
      default: true,
    },
    rrpPrice: {
      type: Number,
      default: 0,
    },
    showCompareAtLabels: {
      type: Boolean,
      default: false,
    },
    varies: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      cnvsGlobal: true,
    }
  },

  computed: {

    /**
     * Compute compare at price template.
     * @returns {String}
     */
    formattedCompareAtPrice() {
      if (!this.compareAtPrice || this.compareAtPrice <= this.price) {
        return ''
      }

      if (this.showCompareAtLabels) {
        return `${this.$string('product.prices.was')} ${this.$formatMoney(this.compareAtPrice)}`
      }

      return this.$formatMoney(this.compareAtPrice)
    },

    /**
     * Compute price template.
     * @returns {String}
     */
    formattedPrice() {
      if (this.price === false) {
        return this.$string('accessibility.loading')
      }

      if (this.price <= 0) {
        return this.$string('product.prices.free')
      }

      if (!this.price) {
        return ''
      }

      if (this.showCompareAtLabels && this.formattedCompareAtPrice) {
        return `${this.$string('product.prices.now')} ${this.$formatMoney(this.price)}`
      }

      return this.$formatMoney(this.price)
    },

    /**
     * Compute RRP price template.
     * - Format RRP price to handle decimals
     * @returns {String}
     */
    formattedRrpPrice() {
      const moneyString = String(this.rrpPrice).split('.')
      const moneyDecimal = moneyString[1]
      let rrpPrice = moneyString[0]

      if (moneyDecimal?.length === 0 || !moneyDecimal) {
        rrpPrice += '00'

      } else if (moneyDecimal?.length === 1) {
        rrpPrice += `${moneyDecimal}0`

      } else if (moneyDecimal?.length === 2) {
        rrpPrice += moneyDecimal
      }

      return `${this.$string('product.prices.rrp')} ${this.$formatMoney(rrpPrice)}`
    },
  },
}
</script>
